import { useReducer } from 'react';
import { Modal, Steps } from 'antd';
import { wizardReducer, initialState } from './wizardReducer';
import Step1Upload from './steps/Step1Upload';
import Step2MapColumns from './steps/Step2MapColumns';
import Step3Preview from './steps/Step3Preview';

interface DirectAllocationImportWizardProps {
	open: boolean;
	payPeriodId: string | null;
	onClose: () => void;
	onImported?: () => void;
	// Pre-supplied file (e.g. attached in the AI chatbot) — see Step1Upload.
	initialFile?: File | null;
}

const STEP_ITEMS = [{ title: 'Upload' }, { title: 'Map Columns' }, { title: 'Preview' }];

/**
 * Bulk "Employee Direct Allocation" file import wizard — Upload -> Map Columns ->
 * Preview. Shell pattern (Modal + sticky Steps + reducer) mirrors
 * EmployeeCost/SmartImport/SmartImportWizard.tsx.
 */
export default function DirectAllocationImportWizard({
	open,
	payPeriodId,
	onClose,
	onImported,
	initialFile,
}: DirectAllocationImportWizardProps) {
	const [state, dispatch] = useReducer(wizardReducer, initialState);
	const companyId = localStorage.getItem('companyId') ?? '';

	const handleClose = () => {
		dispatch({ type: 'RESET' });
		onClose();
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			title="Import Employee Direct Allocation"
			width={1200}
			centered
			maskClosable={false}
			footer={null}
			destroyOnHidden
			styles={{
				body: {
					maxHeight: '85vh',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<Steps
				current={state.currentStep}
				items={STEP_ITEMS}
				style={{
					marginBottom: 24,
					position: 'sticky',
					top: 0,
					background: 'white',
					zIndex: 1,
				}}
			/>
			{state.currentStep === 0 && (
				<Step1Upload state={state} dispatch={dispatch} onClose={handleClose} initialFile={initialFile} />
			)}
			{state.currentStep === 1 && (
				<Step2MapColumns state={state} dispatch={dispatch} onCancel={handleClose} />
			)}
			{state.currentStep === 2 && (
				<Step3Preview
					state={state}
					dispatch={dispatch}
					companyId={companyId}
					payPeriodId={payPeriodId ?? ''}
					onBack={() => dispatch({ type: 'BACK_STEP' })}
					onCancel={handleClose}
					onImported={() => {
						dispatch({ type: 'RESET' });
						onImported?.();
					}}
				/>
			)}
		</Modal>
	);
}
